import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

/**
 * A dialog that opens by itself after a delay to show an offer.
 * With "once per visit", it stays closed for the rest of the visit once the visitor closes it
 * or follows its button. In the theme editor it opens only while its section is selected.
 *
 * @extends DialogComponent
 */
class OfferPopupComponent extends DialogComponent {
  /** @type {number | undefined} */
  #openTimer;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(DialogCloseEvent.eventName, this.rememberDismissed);

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:select', this.#handleSectionSelect);
      document.addEventListener('shopify:section:load', this.#handleSectionSelect);
      document.addEventListener('shopify:section:deselect', this.#handleSectionDeselect);
      return;
    }

    if (this.#wasDismissed()) return;

    // Start loading the photo now, so it's ready when the popup opens
    this.querySelector('img[loading="lazy"]')?.setAttribute('loading', 'eager');
    this.#openTimer = setTimeout(this.#open, this.delay * 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    clearTimeout(this.#openTimer);
    this.removeEventListener(DialogCloseEvent.eventName, this.rememberDismissed);
    document.removeEventListener('shopify:section:select', this.#handleSectionSelect);
    document.removeEventListener('shopify:section:load', this.#handleSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.#handleSectionDeselect);
  }

  /**
   * Keeps the popup closed for the rest of the visit when it's set to show once per visit.
   */
  rememberDismissed = () => {
    if (Shopify.designMode || this.dataset.showAgain !== 'session') return;

    try {
      sessionStorage.setItem(this.#storageKey, 'true');
    } catch {
      // Storage can be unavailable (private browsing); the popup then shows on each page load
    }
  };

  #open = () => {
    // Don't cover a cart drawer, search or menu the visitor already opened
    if (document.querySelector('dialog[open]')) return;

    this.showDialog();
  };

  /**
   * @returns {boolean} Whether the visitor already closed the popup during this visit.
   */
  #wasDismissed() {
    if (this.dataset.showAgain !== 'session') return false;

    try {
      return sessionStorage.getItem(this.#storageKey) === 'true';
    } catch {
      return false;
    }
  }

  /**
   * @param {Event} event - The theme editor's section event.
   */
  #handleSectionSelect = (event) => {
    if (this.#isOwnSection(event)) this.showDialog();
  };

  /**
   * @param {Event} event - The theme editor's section event.
   */
  #handleSectionDeselect = (event) => {
    if (this.#isOwnSection(event)) this.closeDialog();
  };

  /**
   * @param {Event} event - The theme editor's section event.
   * @returns {boolean} Whether the event is about this popup's section.
   */
  #isOwnSection(event) {
    return event instanceof CustomEvent && event.detail?.sectionId === this.dataset.sectionId;
  }

  get #storageKey() {
    return `offer-popup-dismissed:${this.dataset.sectionId}`;
  }

  /**
   * @returns {number} Seconds to wait before opening.
   */
  get delay() {
    return Number(this.dataset.delay) || 0;
  }
}

if (!customElements.get('offer-popup-component')) {
  customElements.define('offer-popup-component', OfferPopupComponent);
}
